import { useCallback, useEffect, useMemo, useState } from "react";
import Templete from "../../template/Template";
import { api } from "../../services/api";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CircleIcon from "@mui/icons-material/Circle";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import ViewKanbanIcon from "@mui/icons-material/ViewKanban";
import RefreshIcon from "@mui/icons-material/Refresh";
import { DragDropContext, Droppable, Draggable, DropResult, DragStart } from "@hello-pangea/dnd";
import { KanbanModulo } from "../Configuracoes/TabKanbanModulos";
import ModalNovaTarefaKanban from "./ModalNovaTarefaKanban";
import {
    KanbanInstancia, InstanciaTarefa,
    STATUS_CONFIG, STATUS_OPTIONS,
    calcularTempoRestante, tarefaTemTimer,
} from "./kanbanTypes";
import { CardInstancia } from "./CardInstancia";
import ContadorAtrasadas from "./ContadorAtrasadas";
import ModalAtrasadas from "./ModalAtrasadas";

export default function Home() {
    const [modulos, setModulos] = useState<KanbanModulo[]>([]);
    const [instancias, setInstancias] = useState<KanbanInstancia[]>([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [dragOrigemIndex, setDragOrigemIndex] = useState<number | null>(null);
    const [infoLogin] = useState(() => JSON.parse(String(sessionStorage.getItem("infoLogin"))));
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [menuTarefa, setMenuTarefa] = useState<InstanciaTarefa | null>(null);

    const [finalizarId, setFinalizarId] = useState<number | null>(null);
    const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
    const [snackMsg, setSnackMsg] = useState("");

    const [retornoPendente, setRetornoPendente] = useState<{ idInstancia: number; moduloDestinoId: number } | null>(null);
    const [justificativaRetorno, setJustificativaRetorno] = useState("");

    const [tempoPendente, setTempoPendente] = useState<{ tarefa: InstanciaTarefa; novoStatus: string } | null>(null);
    const [tempoInput, setTempoInput] = useState("");

    const [editandoInstancia, setEditandoInstancia] = useState<KanbanInstancia | null>(null);
    const [nomeEditInstancia, setNomeEditInstancia] = useState("");
    const [excluindoInstancia, setExcluindoInstancia] = useState<KanbanInstancia | null>(null);

    const [atrasoPendente, setAtrasoPendente] = useState<{ tarefa: InstanciaTarefa; novoStatus: string } | null>(null);
    const [justificativaAtraso, setJustificativaAtraso] = useState("");

    const [modalAtrasadas, setModalAtrasadas] = useState(false);

    const userId: number | undefined = infoLogin?.id_usuario;

    // Índice id_modulo -> posição no array (O(1) lookup)
    const moduloIdxMap = useMemo(() => {
        const m = new Map<number, number>();
        modulos.forEach((mod, idx) => m.set(mod.id_kanban_modulo, idx));
        return m;
    }, [modulos]);

    // Instâncias agrupadas por módulo (evita filter repetido no render)
    const instanciasPorModulo = useMemo(() => {
        const m = new Map<number, KanbanInstancia[]>();
        for (const inst of instancias) {
            const arr = m.get(inst.id_kanban_modulo);
            if (arr) arr.push(inst);
            else m.set(inst.id_kanban_modulo, [inst]);
        }
        return m;
    }, [instancias]);

    // Set de IDs de módulo em que sou participante
    const participanteModuloIds = useMemo(() => {
        const s = new Set<number>();
        for (const m of modulos) {
            if (m.usuarios.some(u => u.id_usuario === userId)) s.add(m.id_kanban_modulo);
        }
        return s;
    }, [modulos, userId]);

    const ultimoModuloId = modulos.length > 0 ? modulos[modulos.length - 1].id_kanban_modulo : null;
    const primeiroModuloId = modulos.length > 0 ? modulos[0].id_kanban_modulo : null;

    const toggleSection = useCallback((key: string) => {
        setCollapsedSections(prev => ({ ...prev, [key]: !(prev[key] ?? true) }));
    }, []);

    useEffect(() => {
        fetchData();
        const interval = setInterval(() => fetchData(), 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    async function fetchData() {
        try {
            const [resModulos, resInstancias] = await Promise.all([
                api.get("/kanbanmodulos"),
                api.get("/kanbaninstancias"),
            ]);
            if (!resModulos.data.error) setModulos(resModulos.data);
            if (!resInstancias.data.error) setInstancias(resInstancias.data);
        } catch (err) {
            console.error("Erro ao carregar dados kanban", err);
        } finally {
            setLoading(false);
        }
    }

    const handleOpenStatusMenu = useCallback((e: React.MouseEvent<HTMLElement>, tarefa: InstanciaTarefa) => {
        setMenuAnchor(e.currentTarget);
        setMenuTarefa(tarefa);
    }, []);

    function handleCloseStatusMenu() {
        setMenuAnchor(null);
        setMenuTarefa(null);
    }

    async function handleChangeStatus(novoStatus: string) {
        if (!menuTarefa) return;
        const tarefa = menuTarefa;
        handleCloseStatusMenu();

        if (novoStatus === "em_execucao" && tarefa.tempo_definido_usuario) {
            setTempoPendente({ tarefa, novoStatus });
            setTempoInput("");
            return;
        }

        if (tarefa.status === "em_execucao" && novoStatus !== "em_execucao" && tarefaTemTimer(tarefa)) {
            const info = calcularTempoRestante(tarefa, Date.now());
            if (info && info.expirado) {
                setAtrasoPendente({ tarefa, novoStatus });
                setJustificativaAtraso("");
                return;
            }
        }

        executarChangeStatus(tarefa.id_kanban_instancia_tarefa, novoStatus);
    }

    async function executarChangeStatus(tarefaId: number, novoStatus: string, tempoHoras?: number, justAtraso?: string) {
        setInstancias(prev =>
            prev.map(inst => {
                const tarefaPertence = inst.tarefas.some(t => t.id_kanban_instancia_tarefa === tarefaId);
                return {
                    ...inst,
                    ...(tarefaPertence && !inst.id_usuario_responsavel ? {
                        id_usuario_responsavel: infoLogin?.id_usuario,
                        nome_responsavel: infoLogin?.nome_usuario,
                    } : {}),
                    tarefas: inst.tarefas.map(t => {
                        if (t.id_kanban_instancia_tarefa !== tarefaId) return t;
                        const tempoFinal = tempoHoras !== undefined ? tempoHoras : t.tempo_execucao_horas;
                        const temTempo = Number(tempoFinal) > 0;
                        return {
                            ...t,
                            status: novoStatus,
                            inicio_execucao: novoStatus === "em_execucao" && temTempo ? new Date().toISOString() : null,
                            ...(tempoHoras !== undefined ? { tempo_execucao_horas: tempoHoras } : {}),
                            ...(justAtraso ? { justificativa_atraso: justAtraso, atrasado_em: new Date().toISOString() } : {}),
                        };
                    }),
                };
            })
        );
        try {
            const body: any = { status: novoStatus };
            if (tempoHoras !== undefined) body.tempo_execucao_horas = tempoHoras;
            if (justAtraso) body.justificativa_atraso = justAtraso;
            const res = await api.put(`/updatekanbaninstanciatarefa/${tarefaId}`, body);
            if (res.data.error) {
                setSnackMsg(res.data.message);
                fetchData();
            }
        } catch {
            fetchData();
        }
    }

    function handleConfirmarAtraso() {
        if (!atrasoPendente || !justificativaAtraso.trim()) return;
        executarChangeStatus(atrasoPendente.tarefa.id_kanban_instancia_tarefa, atrasoPendente.novoStatus, undefined, justificativaAtraso.trim());
        setAtrasoPendente(null);
        setJustificativaAtraso("");
    }

    function handleConfirmarTempo() {
        if (!tempoPendente) return;
        const horas = parseFloat(tempoInput) || 0;
        if (horas <= 0) return;
        executarChangeStatus(tempoPendente.tarefa.id_kanban_instancia_tarefa, tempoPendente.novoStatus, horas);
        setTempoPendente(null);
        setTempoInput("");
    }

    const handleEditarInstancia = useCallback((inst: KanbanInstancia) => {
        setEditandoInstancia(inst);
        setNomeEditInstancia(inst.nome_instancia);
    }, []);

    const handleSetExcluindo = useCallback((inst: KanbanInstancia) => {
        setExcluindoInstancia(inst);
    }, []);

    const handleSetFinalizar = useCallback((id: number) => {
        setFinalizarId(id);
    }, []);

    async function handleSalvarEdicaoInstancia() {
        if (!editandoInstancia || !nomeEditInstancia.trim()) return;
        const id = editandoInstancia.id_kanban_instancia;
        const novoNome = nomeEditInstancia.trim();
        setInstancias(prev => prev.map(i => i.id_kanban_instancia === id ? { ...i, nome_instancia: novoNome } : i));
        setEditandoInstancia(null);
        try {
            await api.put(`/updatekanbaninstancia/${id}`, { nome_instancia: novoNome });
        } catch {
            fetchData();
        }
    }

    async function handleExcluirInstancia() {
        if (!excluindoInstancia) return;
        const id = excluindoInstancia.id_kanban_instancia;
        setInstancias(prev => prev.filter(i => i.id_kanban_instancia !== id));
        setExcluindoInstancia(null);
        try {
            await api.delete(`/deletekanbaninstancia/${id}`);
        } catch {
            fetchData();
        }
    }

    function handleDragStart(start: DragStart) {
        const origemId = Number(start.source.droppableId);
        const idx = moduloIdxMap.get(origemId) ?? -1;
        setDragOrigemIndex(idx);
    }

    async function handleDragEnd(result: DropResult) {
        setDragOrigemIndex(null);
        const { draggableId, source, destination } = result;
        if (!destination) return;
        if (source.droppableId === destination.droppableId) return;

        const idInstancia = Number(draggableId);
        const moduloOrigemId = Number(source.droppableId);
        const moduloDestinoId = Number(destination.droppableId);

        if (!participanteModuloIds.has(moduloOrigemId)) return;

        const idxOrigem = moduloIdxMap.get(moduloOrigemId) ?? -1;
        const idxDestino = moduloIdxMap.get(moduloDestinoId) ?? -1;
        if (idxDestino > idxOrigem) {
            const instancia = instancias.find(i => i.id_kanban_instancia === idInstancia);
            if (instancia) {
                const tarefasDoModuloOrigem = instancia.tarefas.filter(t => t.id_kanban_modulo === moduloOrigemId);
                const todasFinalizadas = tarefasDoModuloOrigem.length > 0 && tarefasDoModuloOrigem.every(t => t.status === "finalizado");
                if (!todasFinalizadas) {
                    setSnackMsg("Finalize todas as tarefas do módulo atual antes de mover o card.");
                    return;
                }

                for (let i = idxOrigem + 1; i < idxDestino; i++) {
                    const moduloPulado = modulos[i];
                    const tarefasDoModuloPulado = instancia.tarefas.filter(t => t.id_kanban_modulo === moduloPulado.id_kanban_modulo);
                    if (tarefasDoModuloPulado.length > 0 && !tarefasDoModuloPulado.every(t => t.status === "finalizado")) {
                        setSnackMsg(`Existem tarefas pendentes no módulo "${moduloPulado.nome_modulo}". Não é possível pular este módulo.`);
                        return;
                    }
                }
            }
        }

        if (idxDestino < idxOrigem) {
            setRetornoPendente({ idInstancia, moduloDestinoId });
            setJustificativaRetorno("");
            return;
        }

        executarMove(idInstancia, moduloDestinoId);
    }

    async function executarMove(idInstancia: number, moduloDestinoId: number, justificativa?: string) {
        setInstancias(prev =>
            prev.map(inst =>
                inst.id_kanban_instancia === idInstancia
                    ? {
                        ...inst,
                        id_kanban_modulo: moduloDestinoId,
                        id_usuario_responsavel: null,
                        nome_responsavel: null,
                        justificativa_retorno: justificativa || null,
                        retornado_em: justificativa ? new Date().toISOString() : null,
                    }
                    : inst
            )
        );

        try {
            await api.put(`/movekanbaninstancia/${idInstancia}`, {
                id_kanban_modulo: moduloDestinoId,
                ...(justificativa ? { justificativa } : {}),
            });
        } catch {
            fetchData();
        }
    }

    function handleConfirmarRetorno() {
        if (!retornoPendente || !justificativaRetorno.trim()) return;
        executarMove(retornoPendente.idInstancia, retornoPendente.moduloDestinoId, justificativaRetorno.trim());
        setRetornoPendente(null);
        setJustificativaRetorno("");
    }

    async function handleFinalizarInstancia() {
        if (!finalizarId) return;
        setInstancias(prev => prev.filter(i => i.id_kanban_instancia !== finalizarId));
        setFinalizarId(null);
        try {
            await api.delete(`/deletekanbaninstancia/${finalizarId}`);
        } catch {
            fetchData();
        }
    }

    return (
        <Templete menuActive="home">
            <Box sx={{ display: "flex", flexDirection: "column", height: "calc(100vh - 68px)", overflow: "hidden" }}>
                {!loading && modulos.length > 0 && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <Button
                            variant="contained"
                            onClick={() => setModalOpen(true)}
                            color="primary"
                        >
                            INICIAR BLOCO
                        </Button>
                        <Tooltip title="Atualizar dados" arrow>
                            <IconButton
                                onClick={() => fetchData()}
                                color="primary"
                                sx={{ border: "1px solid", borderColor: "primary.main" }}
                            >
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                        <Box sx={{ flex: 1 }} />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, bgcolor: "#e3f2fd", border: "1px solid #90caf9", borderRadius: 2, px: 1.5, py: 0.6 }}>
                            <ViewKanbanIcon sx={{ fontSize: 20, color: "#1565c0" }} />
                            <Typography fontSize={14} fontWeight={700} color="#1565c0">
                                {instancias.length}
                            </Typography>
                            <Typography fontSize={13} color="#1565c0">
                                bloco{instancias.length !== 1 ? "s" : ""}
                            </Typography>
                        </Box>
                        <ContadorAtrasadas instancias={instancias} onClick={() => setModalAtrasadas(true)} />
                    </Box>
                )}

                <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                    <Box sx={{
                        display: "flex", gap: 1, flex: 1, overflowX: "auto", overflowY: "hidden", justifyContent: "flex-start", minWidth: 0, maxWidth: "100%",
                        "&::-webkit-scrollbar": { height: 6 },
                        "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
                        "&::-webkit-scrollbar-thumb": { bgcolor: "#bdbdbd", borderRadius: 3, "&:hover": { bgcolor: "#9e9e9e" } },
                        scrollbarWidth: "thin", scrollbarColor: "#bdbdbd transparent",
                    }}>
                        {loading ? (
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
                                <CircularProgress />
                            </Box>
                        ) : modulos.length === 0 ? (
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
                                <Box sx={{ textAlign: "center", maxWidth: 420 }}>
                                    <Box
                                        component="img"
                                        src="/logo192.png"
                                        alt="SysproJud"
                                        sx={{ width: 100, height: 100, mb: 3, opacity: 0.85 }}
                                    />
                                    <Typography fontSize={26} fontWeight={700} color="#091114" sx={{ mb: 1 }}>
                                        Bem-vindo ao SysproJud
                                    </Typography>
                                    <Typography fontSize={15} color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                                        Organize seu fluxo de trabalho de forma inteligente.
                                        Gerencie tarefas, acompanhe prazos e alcance resultados com mais eficiência.
                                    </Typography>
                                    <Typography fontSize={13} color="#009BE5" fontWeight={500}>
                                        Para começar, acesse Configurações e crie seus módulos Kanban.
                                    </Typography>
                                </Box>
                            </Box>
                        ) : (
                            modulos.map((modulo, moduloIndex) => {
                                const participante = participanteModuloIds.has(modulo.id_kanban_modulo);
                                const instanciasDoModulo = instanciasPorModulo.get(modulo.id_kanban_modulo) || [];
                                return (
                                    <Box
                                        key={modulo.id_kanban_modulo}
                                        sx={{
                                            width: 320,
                                            minWidth: 320,
                                            flexShrink: 0,
                                            height: "100%",
                                            bgcolor: modulo.cor_corpo || "#FFFFFF",
                                            borderRadius: 2,
                                            display: "flex",
                                            flexDirection: "column",
                                            overflow: "hidden",
                                            boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                bgcolor: modulo.cor_header || "#000000",
                                                color: "#fff",
                                                px: 2,
                                                py: 1,
                                                display: "flex",
                                                alignItems: "center",
                                            }}
                                        >
                                            <Box sx={{ flex: 1, textAlign: "center" }}>
                                                <Typography fontWeight={600} fontSize={15}>
                                                    {modulo.nome_modulo}
                                                </Typography>
                                                <Typography fontSize={11} sx={{ opacity: 0.8 }}>
                                                    {modulo.usuarios.length} participante{modulo.usuarios.length !== 1 ? "s" : ""}
                                                </Typography>
                                            </Box>
                                            <Box
                                                sx={{
                                                    bgcolor: "rgba(255,255,255,0.2)",
                                                    borderRadius: "50%",
                                                    width: 28,
                                                    height: 28,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <Typography fontSize={13} fontWeight={700}>
                                                    {instanciasDoModulo.length}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Droppable droppableId={String(modulo.id_kanban_modulo)}>
                                            {(provided, snapshot) => {
                                                let dropBg = "transparent";
                                                if (snapshot.isDraggingOver && dragOrigemIndex !== null) {
                                                    if (moduloIndex > dragOrigemIndex) dropBg = "#c8e6c9";
                                                    else if (moduloIndex < dragOrigemIndex) dropBg = "#ffcdd2";
                                                }
                                                return (
                                                    <Box
                                                        ref={provided.innerRef}
                                                        {...provided.droppableProps}
                                                        sx={{
                                                            flex: 1,
                                                            p: 1,
                                                            overflowY: "auto",
                                                            bgcolor: dropBg,
                                                            transition: "background-color 0.2s",
                                                            minHeight: 50,
                                                            "&::-webkit-scrollbar": { width: 5 },
                                                            "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
                                                            "&::-webkit-scrollbar-thumb": { bgcolor: "#bdbdbd", borderRadius: 3, "&:hover": { bgcolor: "#9e9e9e" } },
                                                            scrollbarWidth: "thin", scrollbarColor: "#bdbdbd transparent",
                                                        }}
                                                    >
                                                        {instanciasDoModulo.map((inst, index) => {
                                                            const cardKey = `card-${inst.id_kanban_instancia}`;
                                                            const cardCollapsed = collapsedSections[cardKey] ?? true;
                                                            const noUltimoModulo = inst.id_kanban_modulo === ultimoModuloId;
                                                            const tudoFinalizado = inst.tarefas.length > 0 && inst.tarefas.every(t => t.status === "finalizado");
                                                            return (
                                                                <Draggable
                                                                    key={inst.id_kanban_instancia}
                                                                    draggableId={String(inst.id_kanban_instancia)}
                                                                    index={index}
                                                                    isDragDisabled={!participante}
                                                                >
                                                                    {(provided, snapshot) => (
                                                                        <CardInstancia
                                                                            inst={inst}
                                                                            provided={provided}
                                                                            snapshot={snapshot}
                                                                            participante={participante}
                                                                            idxModuloAtual={moduloIndex}
                                                                            moduloIdxMap={moduloIdxMap}
                                                                            userId={userId}
                                                                            cardCollapsed={cardCollapsed}
                                                                            sectionsCollapsed={collapsedSections}
                                                                            podeFinalizar={noUltimoModulo && tudoFinalizado}
                                                                            estaNoPrimeiroModulo={inst.id_kanban_modulo === primeiroModuloId}
                                                                            onToggleCard={toggleSection}
                                                                            onToggleSection={toggleSection}
                                                                            onOpenStatusMenu={handleOpenStatusMenu}
                                                                            onEditar={handleEditarInstancia}
                                                                            onExcluir={handleSetExcluindo}
                                                                            onFinalizar={handleSetFinalizar}
                                                                        />
                                                                    )}
                                                                </Draggable>
                                                            );
                                                        })}
                                                        {provided.placeholder}
                                                    </Box>
                                                );
                                            }}
                                        </Droppable>
                                    </Box>
                                );
                            })
                        )}
                    </Box>
                </DragDropContext>
            </Box>

            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleCloseStatusMenu}
                slotProps={{ paper: { sx: { minWidth: 180 } } }}
            >
                {STATUS_OPTIONS.map(key => {
                    const cfg = STATUS_CONFIG[key];
                    const selected = menuTarefa?.status === key;
                    return (
                        <MenuItem
                            key={key}
                            onClick={() => handleChangeStatus(key)}
                            selected={selected}
                            dense
                        >
                            <ListItemIcon sx={{ minWidth: 28 }}>
                                <CircleIcon sx={{ fontSize: 10, color: cfg.color }} />
                            </ListItemIcon>
                            <ListItemText primaryTypographyProps={{ fontSize: 13 }}>
                                {cfg.label}
                            </ListItemText>
                        </MenuItem>
                    );
                })}
            </Menu>

            <ModalNovaTarefaKanban
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onSaved={fetchData}
            />

            <Dialog open={finalizarId !== null} onClose={() => setFinalizarId(null)}>
                <DialogTitle>Finalizar tarefa</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Todas as tarefas foram concluídas. Deseja finalizar este card? Ele será removido do board.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFinalizarId(null)}>Cancelar</Button>
                    <Button onClick={handleFinalizarInstancia} variant="contained" color="success">
                        Finalizar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={retornoPendente !== null} onClose={() => setRetornoPendente(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Justificativa de retorno</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Informe o motivo para retornar este card ao módulo anterior.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        minRows={3}
                        label="Justificativa"
                        value={justificativaRetorno}
                        onChange={e => setJustificativaRetorno(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRetornoPendente(null)}>Cancelar</Button>
                    <Button
                        onClick={handleConfirmarRetorno}
                        variant="contained"
                        color="warning"
                        disabled={!justificativaRetorno.trim()}
                    >
                        Confirmar retorno
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={editandoInstancia !== null} onClose={() => setEditandoInstancia(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Editar bloco</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Nome"
                        value={nomeEditInstancia}
                        onChange={e => setNomeEditInstancia(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") handleSalvarEdicaoInstancia(); }}
                        inputProps={{ maxLength: 255 }}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditandoInstancia(null)}>Cancelar</Button>
                    <Button onClick={handleSalvarEdicaoInstancia} variant="contained" disabled={!nomeEditInstancia.trim()}>
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={excluindoInstancia !== null} onClose={() => setExcluindoInstancia(null)}>
                <DialogTitle>Excluir bloco</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Deseja excluir o bloco <strong>{excluindoInstancia?.nome_instancia}</strong>? Esta ação não pode ser desfeita.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setExcluindoInstancia(null)}>Cancelar</Button>
                    <Button onClick={handleExcluirInstancia} variant="contained" color="error">
                        Excluir
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={atrasoPendente !== null} onClose={() => setAtrasoPendente(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Justificativa de atraso</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        O tempo de execução da tarefa <strong>{atrasoPendente?.tarefa.nome_tarefa}</strong> foi excedido. Informe o motivo do atraso.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        minRows={3}
                        label="Motivo do atraso"
                        value={justificativaAtraso}
                        onChange={e => setJustificativaAtraso(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAtrasoPendente(null)}>Cancelar</Button>
                    <Button
                        onClick={handleConfirmarAtraso}
                        variant="contained"
                        color="error"
                        disabled={!justificativaAtraso.trim()}
                    >
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={tempoPendente !== null} onClose={() => setTempoPendente(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Tempo de execução</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Informe o tempo estimado de execução para a tarefa <strong>{tempoPendente?.tarefa.nome_tarefa}</strong>.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        fullWidth
                        type="number"
                        label="Horas"
                        value={tempoInput}
                        onChange={e => setTempoInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleConfirmarTempo(); }}
                        InputProps={{
                            endAdornment: <InputAdornment position="end">h</InputAdornment>,
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTempoPendente(null)}>Cancelar</Button>
                    <Button
                        onClick={handleConfirmarTempo}
                        variant="contained"
                        disabled={!tempoInput || parseFloat(tempoInput) <= 0}
                    >
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>

            <ModalAtrasadas
                open={modalAtrasadas}
                onClose={() => setModalAtrasadas(false)}
                instancias={instancias}
                modulos={modulos}
            />

            <Snackbar
                open={!!snackMsg}
                autoHideDuration={4000}
                onClose={() => setSnackMsg("")}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert onClose={() => setSnackMsg("")} severity="warning" variant="filled" sx={{ width: "100%" }}>
                    {snackMsg}
                </Alert>
            </Snackbar>
        </Templete>
    );
}
